<script lang="ts" setup>
import { computed, ref, onBeforeMount, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useAlerts } from '@/composables/alert/useAlerts'
import { useAuthBff } from '@/composables/bff/useAuthBff'
import { useI18n } from 'vue-i18n'
import { useDebounceFn } from '@vueuse/core'
import fetchApi from '@/utils/fetchApi.ts'
interface TokenResponse {
  token: string
  url: string
}

interface IframeMessage {
  type: string
  height?: number
  message?: string
}

const router = useRouter()
const { addAlert, clearAlerts } = useAlerts()
const { checkAuth, primaryLanguage } = useAuthBff()
const { t, locale } = useI18n({ useScope: 'global' })

const token = ref('')
const iframeUrl = ref('')
const loading = ref(true)
const error = ref('')
const isRefreshing = ref(false)
const isIframeRefreshing = ref(false)
const isFramePending = ref(true)
const showModalBackdrop = ref(false)
const showDiscardConfirmation = ref(false)
const iframeRef = ref<HTMLIFrameElement | null>(null)
const showFrameLoader = computed(() => isFramePending.value || isIframeRefreshing.value)

const generateToken = async () => {
  try {
    const response = await fetchApi<TokenResponse>({
      endpoint: '/profile/request-account-session',
      method: 'POST',
      body: {
        locale: locale.value,
      },
    })
    token.value = response.token
    iframeUrl.value = response.url
    error.value = ''
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'An error occurred'
    throw err
  }
}

const refreshToken = async () => {
  if (isRefreshing.value) {
    return
  }
  isRefreshing.value = true

  try {
    const currentUrl = iframeUrl.value
    iframeUrl.value = ''

    void debouncedFetch()

    if (iframeUrl.value === currentUrl) {
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  } catch (err) {
    console.error('Failed to refresh token:', err)
    addAlert('error_occurred_try_again', 'danger')
  } finally {
    isRefreshing.value = false
  }
}
// need to debounce to be sure locale is properly loaded
const debouncedFetch = useDebounceFn(() => {
  generateToken()
    .catch((err: unknown) => {
      console.error('Failed to fetch account session:', err)
      addAlert('error_occurred_try_again', 'danger')
      isFramePending.value = false
    })
    .finally(() => {
      loading.value = false
    })
}, 100)

onBeforeMount(() => {
  void debouncedFetch()
})

const sendMessageToIframe = (message: { type: string; [key: string]: unknown }) => {
  if (iframeRef.value?.contentWindow) {
    iframeRef.value.contentWindow.postMessage(message, '*')
  }
}

const confirmDiscard = () => {
  showDiscardConfirmation.value = false
  showModalBackdrop.value = false
  sendMessageToIframe({ type: 'DISCARD_CONFIRMED' })
  return true
}

const keepEditing = () => {
  showDiscardConfirmation.value = false
  showModalBackdrop.value = false
  sendMessageToIframe({ type: 'KEEP_EDITING' })
  return true
}

const onIframeLoad = () => {
  isFramePending.value = false
  isIframeRefreshing.value = false
}

const onMessage = (event: MessageEvent) => {
  const data = event.data as IframeMessage
  const { type, height } = data
  switch (type) {
    case 'resize':
      if (iframeRef.value && typeof height === 'number') {
        iframeRef.value.style.height = `${String(height)}px`
      }
      break
    case 'password-changed':
      clearAlerts()
      addAlert('password_changed_successfully', 'success')
      break
    case 'profile-updated':
      isIframeRefreshing.value = !!iframeUrl.value
      isFramePending.value = true
      fetchApi({ endpoint: '/auth/refresh-identity', method: 'POST' })
        .then(() => checkAuth())
        .then(() => {
          const newLang = primaryLanguage.value
          const currentLocale = router.currentRoute.value.params.locale as string
          if (newLang && newLang !== currentLocale) {
            const prefix = `/${currentLocale}`
            const newPath = `/${newLang}${router.currentRoute.value.path.slice(prefix.length)}`
            locale.value = newLang
            return router.replace(newPath).then(() => {
              return generateToken()
            })
          }
          onIframeLoad()
        })
        .catch(() => {
          onIframeLoad()
        })
        .finally(() => {
          clearAlerts()
          addAlert('profile_updated_successfully', 'success')
        })
      break
    case 'error':
      clearAlerts()
      if (data.message) {
        addAlert(data.message, 'only_message')
      } else {
        addAlert('error_occurred_try_again', 'danger')
      }
      break
    case 'token-expired':
      void refreshToken()
      break
    case 'REQUEST_DISCARD_CONFIRMATION':
      showDiscardConfirmation.value = true
      showModalBackdrop.value = true
      break
  }
}

onMounted(() => {
  window.addEventListener('message', onMessage)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage)
})
</script>

<template>
  <div class="relative min-h-full" data-testid="account-session-frame-wrapper">
    <iframe
      v-if="iframeUrl"
      ref="iframeRef"
      :src="iframeUrl"
      sandbox="allow-forms allow-scripts allow-same-origin"
      allow="clipboard-write"
      scrolling="no"
      class="block w-full overflow-hidden border-0 transition-opacity duration-200 ease-in-out"
      :class="showFrameLoader ? 'opacity-0' : 'opacity-100'"
      title="Account Management"
      @load="onIframeLoad"
    />

    <Transition
      enter-active-class="transition-opacity duration-200 ease-in-out"
      leave-active-class="transition-opacity duration-200 ease-in-out"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showFrameLoader"
        class="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px] pointer-events-none"
        :data-testid="
          isIframeRefreshing ? 'account-session-refresh-overlay' : 'account-session-initial-loader'
        "
      >
        <OSpinner size="XL" />
      </div>
    </Transition>
  </div>

  <OModal
    v-model:open="showDiscardConfirmation"
    :title="t('unsaved_changes')"
    :primary-action-title="t('discard_changes')"
    :secondary-action-title="t('cancel')"
    :primary-action-promise="confirmDiscard"
    :secondary-action-promise="keepEditing"
    primary-action-enabled
    secondary-action-enabled
    danger
  >
    <template #content>
      <span class="text-neutral-1">{{ t('unsaved_changes_warning') }}</span>
    </template>
  </OModal>
</template>
